import { defineStore, storeToRefs } from 'pinia';
import types from '@/types';
import AppService from '@/services/AppService';
import RequestService from '@/services/RequestService';
import useContractorStore from '@/stores/contractor.store';
import useAuthStore from '@/stores/auth.store';

const useAppService = new AppService();
const useRequestService = new RequestService();

export const useAppStore = defineStore('app', {
  state: () => ({
    notification: {
      title: null,
      message: null,
      color: 'info',
      show: false,
      timex: 10000,
    },
    customerMerchantInfo: {
      sapCustomer: null,
      sapCustomerFiscal: null,
      sapCustomerName: null,

      merchantNumber: null,
      merchantName: null,
      advancePayments: [],
      items: [],
    },
    exchangeRate: null,
    fileConfigCache: null,
  }),
  actions: {
    [types.CLOSE_NOTIFICATION]() {
      this.notification.show = false;
    },
    [types.SHOW_NOTIFICATION]({
      title, message, timex = 10000, type = 'success',
    }) {
      this[types.CLOSE_NOTIFICATION]();
      this.notification.title = title;
      this.notification.message = message;
      this.notification.show = true;
      this.notification.timex = timex;
      this.notification.type = type;
    },
    async [types.GET_FILE_CONFIG](filter = null) {
      const CACHE_KEY = 'file_config_cache';
      const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

      try {
        // Check memory cache first
        if (this.fileConfigCache) {
          const { data, timestamp } = this.fileConfigCache;
          if (Date.now() - timestamp < CACHE_TTL) {
            if (filter !== null) {
              return data.filter((config) => config[filter] === 'X');
            }
            return { data };
          }
        }

        // Check localStorage cache
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          if (Date.now() - timestamp < CACHE_TTL) {
            // Update memory cache
            this.fileConfigCache = { data, timestamp };
            if (filter !== null) {
              return data.filter((config) => config[filter] === 'X');
            }
            return { data };
          }
        }

        // Fetch fresh data from API
        const response = await useAppService.getFileConfig();

        // Cache the response
        const cacheEntry = {
          data: response.data,
          timestamp: Date.now(),
        };

        this.fileConfigCache = cacheEntry;
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));

        if (filter !== null) {
          return response.data.filter((config) => config[filter] === 'X');
        }
        return response;
      } catch (error) {
        console.error('Error fetching file config:', error);

        // Try to use stale cache as fallback
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const { data } = JSON.parse(cachedData);
          console.warn('Using stale cache due to error');

          this[types.SHOW_NOTIFICATION]({
            title: 'Advertencia',
            message: 'No se pudo cargar la configuración actualizada. Usando datos en caché.',
            type: 'warning',
            timex: 5000,
          });

          if (filter !== null) {
            return data.filter((config) => config[filter] === 'X');
          }
          return { data };
        }

        // No cache available - show error
        this[types.SHOW_NOTIFICATION]({
          title: 'Error',
          message: 'No se pudo cargar la configuración de archivos. Por favor, intente nuevamente.',
          type: 'error',
          timex: 8000,
        });

        throw error;
      }
    },
    async [types.SETUP_APP]() {
      const contractorStore = useContractorStore();
      const authStore = useAuthStore();

      const { contractor } = storeToRefs(contractorStore);
      const { userInfo } = storeToRefs(authStore);

      if (userInfo.value === null) {
        await authStore[types.GET_USER]();
      }

      if (!contractorStore.isInitialized) {
        await contractorStore[types.GET_CONTRACTOR]();
      }

      if (authStore[types.IS_LOGGED_IN]()) {
        this.customerMerchantInfo.sapCustomer = import.meta.env.VITE_SAP_CUSTOMER;
        this.customerMerchantInfo.sapCustomerFiscal = import.meta.env.VITE_SAP_FISCAL_CUSTOMER;
        this.customerMerchantInfo.merchantNumber = import.meta.env.VITE_MERCHANT_NUMBER;
        this.customerMerchantInfo.merchantName = import.meta.env.VITE_MERCHANT_NAME;

        if (this.exchangeRate === null) {
          const exchangeData = await useRequestService.get_exchange_rate();
          this.exchangeRate = exchangeData.rate;
        }
      }
    },
    async [types.GET_CONTRACTOR_MATERIAL](id) {
      return useAppService.getContractorMaterial(id);
    },
  },
});
